import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useDiscreto } from './sensivel';
import { flushSync } from 'react-dom';

/**
 * Virtualização da `Tabela` por blocos (r5 robustez B8). Depois de 20 "Carregar mais" eram ~33 mil nós e cada clique
 * levava de 2,4 a 4,1 s. As linhas carregadas são divididas em blocos de `TAM_BLOCO`; o bloco fora da janela (a tela mais
 * `MARGEM` acima e abaixo) é trocado por um espaço da mesma altura, então a rolagem não pula e o DOM fica com poucas
 * dezenas de linhas, não com milhares. Altura variável (linha que quebra texto) funciona porque cada bloco mede a si mesmo.
 *
 * Sem biblioteca: `@tanstack/react-virtual` não está no repositório (lockfile) e o bloco cobre a necessidade com IntersectionObserver.
 * Decisões e limites:
 *  - Bloco com o foco dentro nunca é desmontado (teclado e leitor de tela não perdem o lugar), e os vizinhos dele ficam
 *    montados (r7 visual B3: o Tab saindo da última linha de um bloco caía no bloco seguinte ainda desmontado e pulava linhas).
 *  - Custo (r6 robustez B1): o bloco recebe uma função de render, chamada só quando ele está montado, e é `memo`; os blocos
 *    são estáveis por página (`blocosPorPagina`). Digitar na busca ou carregar mais não renderiza de novo as linhas já vistas.
 *  - Impressão: em `beforeprint` todos os blocos são montados (e as duas versões, tabela e cartões, como antes: o CSS escolhe).
 *  - Ctrl+F do navegador só encontra o que está montado; a busca da própria tela (servidor) continua valendo para tudo.
 *  - Acessibilidade: `aria-rowcount` (-1 quando há mais páginas) e `aria-rowindex` na tabela; `aria-setsize` e `aria-posinset` nos cartões.
 */
export const TAM_BLOCO = 25;
const MARGEM = '1200px 0px';
const BLOCOS_INICIAIS = 2;
const ALTURA_PADRAO = 56;

/** Divide a lista em blocos consecutivos de `tam` itens (o último pode ser menor). */
export function dividirEmBlocos<T>(lista: readonly T[], tam = TAM_BLOCO): T[][] {
  const blocos: T[][] = [];
  for (let i = 0; i < lista.length; i += tam) blocos.push(lista.slice(i, i + tam));
  return blocos;
}

export interface Bloco<T> {
  /** Chave estável (id da primeira linha). */
  chave: string;
  /** Posição da primeira linha na lista inteira. */
  inicio: number;
  linhas: T[];
}

/**
 * Blocos de todas as páginas, estáveis por página: a página que não mudou devolve os mesmos arrays de linhas (cache por objeto
 * de página), então o `memo` do bloco segura o render ao carregar mais ou ao digitar na busca.
 */
export function blocosPorPagina<T extends { id: string }>(paginas: readonly { itens: T[] }[], cache: WeakMap<object, T[][]>, tam = TAM_BLOCO): Bloco<T>[] {
  const saida: Bloco<T>[] = [];
  let inicio = 0;
  for (const p of paginas) {
    let blocos = cache.get(p);
    if (!blocos) {
      blocos = dividirEmBlocos(p.itens, tam);
      cache.set(p, blocos);
    }
    for (const linhas of blocos) {
      if (linhas.length) saida.push({ chave: linhas[0]!.id, inicio, linhas });
      inicio += linhas.length;
    }
  }
  return saida;
}

/** Blocos que ficam montados por causa do foco: o que tem o foco e os vizinhos (o Tab chega a eles sem pular linha). */
export function perto(indice: number, comFoco: number | null): boolean {
  return comFoco !== null && Math.abs(indice - comFoco) <= 1;
}

/** `aria-rowcount` da tabela: linhas carregadas mais o cabeçalho; -1 (desconhecido) enquanto houver próxima página. */
export function totalDeLinhasAria(carregadas: number, temMais: boolean): number {
  return temMais ? -1 : carregadas + 1;
}

/** Altura estimada de um bloco ainda não medido, a partir da média por linha já vista. */
export function alturaEstimada(linhasNoBloco: number, mediaPorLinha: number | null): number {
  return Math.round(linhasNoBloco * (mediaPorLinha && mediaPorLinha > 0 ? mediaPorLinha : ALTURA_PADRAO));
}

const CONSULTA_DESKTOP = '(min-width: 768px)';

/** Verdadeiro a partir de `md` (768px): só a versão do layout em uso fica no DOM. */
export function useDesktop(): boolean {
  const [desktop, setDesktop] = useState(() => (typeof window === 'undefined' || !window.matchMedia ? true : window.matchMedia(CONSULTA_DESKTOP).matches));
  useEffect(() => {
    if (!window.matchMedia) return;
    const m = window.matchMedia(CONSULTA_DESKTOP);
    const mudou = () => setDesktop(m.matches);
    mudou();
    m.addEventListener('change', mudou);
    return () => m.removeEventListener('change', mudou);
  }, []);
  return desktop;
}

/** Verdadeiro durante a impressão: tudo é montado (`flushSync` para o conteúdo existir antes de o navegador paginar). */
export function useImprimindo(): boolean {
  const [imprimindo, setImprimindo] = useState(false);
  useEffect(() => {
    const antes = () => flushSync(() => setImprimindo(true));
    const depois = () => setImprimindo(false);
    window.addEventListener('beforeprint', antes);
    window.addEventListener('afterprint', depois);
    return () => {
      window.removeEventListener('beforeprint', antes);
      window.removeEventListener('afterprint', depois);
    };
  }, []);
  return imprimindo;
}

/** Média de altura por linha, compartilhada pelos blocos da mesma tabela (estimativa dos que ainda não foram vistos). */
export function useMediaDeLinha() {
  const media = useRef<number | null>(null);
  const registrar = useCallback((altura: number, linhas: number) => {
    if (linhas > 0 && altura > 0) media.current = media.current === null ? altura / linhas : (media.current * 3 + altura / linhas) / 4;
  }, []);
  return { media, registrar };
}

interface PropsBloco<T> {
  indice: number;
  linhas: T[];
  inicio: number;
  tag: 'tbody' | 'div';
  /** Colunas da tabela, para o espaço que substitui o bloco (só `tbody`). */
  colunas?: number;
  forcar?: boolean;
  /** Vizinho do bloco com o foco: fica montado mesmo longe da janela. */
  pertoDoFoco?: boolean;
  media: RefObject<number | null>;
  aoMedir: (altura: number, linhas: number) => void;
  className?: string;
  /** Chamada só com o bloco montado. Precisa ser estável (useCallback) para o memo valer. */
  renderizar: (linhas: T[], inicio: number) => ReactNode;
}

/** Bloco de linhas: monta o conteúdo perto da janela e, longe dela, deixa só um espaço com a altura que tinha. */
function BlocoVirtualBase<T>({ indice, linhas, inicio, tag, colunas = 1, forcar, pertoDoFoco, media, aoMedir, className, renderizar }: PropsBloco<T>) {
  // Modo discreto: o memo do bloco não pode segurar valores formatados antes da troca.
  useDiscreto();
  const ref = useRef<HTMLElement>(null);
  const [visivel, setVisivel] = useState(indice < BLOCOS_INICIAIS);
  const altura = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisivel(true);
      return;
    }
    const io = new IntersectionObserver(
      (entradas) => {
        const dentro = entradas[entradas.length - 1]?.isIntersecting ?? false;
        // O foco dentro do bloco o mantém montado.
        setVisivel(dentro || el.contains(document.activeElement));
      },
      { rootMargin: MARGEM },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const montado = visivel || !!forcar || !!pertoDoFoco;
  // Mede o bloco montado (e remede quando o conteúdo ou a largura mudam): é a altura do espaço quando ele sair.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!montado || !el) return;
    const medir = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) {
        altura.current = h;
        aoMedir(h, linhas.length);
      }
    };
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [montado, linhas.length, aoMedir]);

  const h = altura.current ?? alturaEstimada(linhas.length, media.current);
  if (tag === 'tbody') {
    return (
      <tbody ref={ref as RefObject<HTMLTableSectionElement>} data-bloco={indice} className={className}>
        {montado ? (
          renderizar(linhas, inicio)
        ) : (
          <tr aria-hidden="true">
            <td colSpan={colunas} style={{ height: h, padding: 0, border: 0 }} />
          </tr>
        )}
      </tbody>
    );
  }
  return (
    <div ref={ref as RefObject<HTMLDivElement>} role="presentation" data-bloco={indice} className={className} style={montado ? undefined : { height: h }}>
      {montado ? renderizar(linhas, inicio) : null}
    </div>
  );
}

export const BlocoVirtual = memo(BlocoVirtualBase) as typeof BlocoVirtualBase;

/** Índice do bloco (`data-bloco`) que tem o foco dentro de `raiz`, para manter os vizinhos montados. */
export function useBlocoComFoco(raiz: RefObject<HTMLElement | null>) {
  const [comFoco, setComFoco] = useState<number | null>(null);
  useEffect(() => {
    const el = raiz.current;
    if (!el) return;
    const entrou = (e: FocusEvent) => {
      const b = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-bloco]');
      setComFoco(b && el.contains(b) ? Number(b.dataset.bloco) : null);
    };
    el.addEventListener('focusin', entrou);
    return () => el.removeEventListener('focusin', entrou);
  }, [raiz]);
  return comFoco;
}

/**
 * Larguras mínimas por coluna que só crescem (r5 robustez B8): com blocos montando e saindo, a largura automática das colunas
 * mudaria conforme o conteúdo visível; guardar o maior já visto de cada uma mantém o cabeçalho parado. Zera ao trocar as colunas.
 */
export function useLargurasEstaveis(tabela: RefObject<HTMLTableElement | null>, chaveColunas: string) {
  const [larguras, setLarguras] = useState<number[]>([]);
  useEffect(() => {
    setLarguras([]);
  }, [chaveColunas]);
  useEffect(() => {
    const el = tabela.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const medir = () => {
      const ths = [...el.querySelectorAll<HTMLElement>('thead th')];
      setLarguras((antes) => {
        let mudou = ths.length !== antes.length;
        const novas = ths.map((th, i) => {
          // Review r16 visual B4: para baixo (com o arredondamento para cima, a soma das colunas passava a caixa em 1 a 7 px e a
          // tabela ganhava rolagem horizontal sem precisar).
          const w = Math.floor(th.getBoundingClientRect().width);
          const a = antes[i] ?? 0;
          if (w > a + 1) {
            mudou = true;
            return w;
          }
          return a;
        });
        return mudou ? novas : antes;
      });
    };
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    const aoRedimensionar = () => setLarguras([]);
    window.addEventListener('resize', aoRedimensionar);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', aoRedimensionar);
    };
  }, [tabela, chaveColunas]);
  return larguras;
}
